import { ArrowUpRight, ImageOff } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";

import { cn, insetFocusRingClassName } from "@ceedero/ui";

import { useTranslation } from "@/localization/hooks/use-translation";
import { ProjectCoverTransition } from "@/music-catalog/components/project-cover-transition";
import { formatCatalogDate } from "@/music-catalog/utils/catalog-view";
import { resolveProjectArtworkPanels } from "@/music-catalog/utils/project-artwork";
import { resolveProjectDiscWarmUpHandlers } from "@/music-catalog/utils/project-disc-warm-up";
import { ROUTES } from "@/navigation/constants/routes";

import type { LibraryProject } from "@/cloud/types/types";

/**
 * One entry in the index of recent saves, set like a contents page: its place in
 * the running order, the cover, the name at display size, and when it arrived.
 *
 * The thumbnail is the Front or nothing. A legacy project can carry Disc artwork
 * without a Front, and that differently shaped picture must not stand in for the
 * cover it is not.
 */
export const RecentProjectRow = ({ project, rank }: { project: LibraryProject; rank: number }) => {
  const { language, t } = useTranslation();
  const [hasCoverFailed, setCoverFailed] = useState(false);
  const front = resolveProjectArtworkPanels(project).find((panel) => panel.id === "front");
  const updated = formatCatalogDate(project.updatedAt, language);
  const trackCount = project.trackCount ?? project.tracks.length;

  return (
    <Link
      to={`${ROUTES.catalog}/${encodeURIComponent(project.desktopProjectId)}`}
      className={cn(
        "group grid grid-cols-[auto_auto_minmax(0,1fr)_auto] items-center gap-4 border-t border-border px-2 py-3 transition-colors duration-200 hover:bg-accent/30 motion-reduce:transition-none sm:gap-5",
        insetFocusRingClassName,
      )}
      {...resolveProjectDiscWarmUpHandlers(project)}
    >
      <span className="w-8 font-display text-2xl font-semibold text-muted-foreground/50 tabular-nums transition-colors group-hover:text-primary motion-reduce:transition-none">
        {String(rank).padStart(2, "0")}
      </span>

      <span className="size-14 shrink-0 overflow-hidden rounded-[2px] bg-muted ring-1 ring-border transition-transform duration-300 ease-out group-hover:scale-110 group-hover:-rotate-3 motion-reduce:transition-none motion-reduce:group-hover:transform-none sm:size-16">
        {front === undefined || hasCoverFailed ? (
          <span data-cover-placeholder className="flex size-full items-center justify-center">
            <ImageOff aria-hidden className="size-6 text-muted-foreground" />
          </span>
        ) : (
          <ProjectCoverTransition desktopProjectId={project.desktopProjectId}>
            <img
              src={front.url}
              alt=""
              loading="lazy"
              className="size-full object-contain"
              onError={() => setCoverFailed(true)}
            />
          </ProjectCoverTransition>
        )}
      </span>

      <span className="min-w-0">
        <span className="block truncate font-display text-lg font-semibold tracking-tight sm:text-xl">
          {project.title}
        </span>
        <span className="block truncate text-xs text-muted-foreground">
          {t("catalog.trackCount", { count: trackCount })}
          {updated === undefined ? null : (
            <span className="sm:hidden"> · {t("catalog.updated", { date: updated })}</span>
          )}
        </span>
      </span>

      <span className="flex items-center gap-3 text-sm text-muted-foreground">
        {updated !== undefined && <span className="hidden sm:inline">{updated}</span>}
        <ArrowUpRight
          aria-hidden
          className="size-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-foreground motion-reduce:transition-none"
        />
      </span>
    </Link>
  );
};
